import { WaitingRoomContainer } from "../../../components/pedagogy/WaitingRoom";
import { useMeetingSelector, useMultiMeetingAPI } from "../../../hooks/meeting";
import { usePedagogyAvailableViews, usePopulatedMeetingData } from "../../../hooks/populatedData";
import { useUser } from "../../../store/session/hooks";

import { MeetingContainer } from "@kalyzee/kast-web-sdk";
import { useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { MeetingRoomStatus } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PedagogyBottomBar } from "../../../components/pedagogy/BottomBar";
import { PedagogyMessageForm } from "../../../components/pedagogy/MessageForm";
import { getPedagogyAudioValidator } from "../../../helpers/audio";
import { sdkClient } from "../../../helpers/sdk";
import { usePedagogyContainerAPI, usePedagogyUsers } from "../../../hooks/pedagogy";
import styles from "./communication.module.css";
import { usePedagogyContext } from "./context";

export const MeetingWaitingRoomPage = () => {
  const pedagogyCtx = usePedagogyContext();
  const pedagogyCtxRef = useRefAutoUpdate(pedagogyCtx);

  const selfUser = useUser();

  const pedagoAPI = usePedagogyContainerAPI(pedagogyCtx);
  const populatedData = usePopulatedMeetingData(pedagoAPI);
  const views = usePedagogyAvailableViews(populatedData, selfUser?.id);
  const currentView = views[0];

  const [session] = useMeetingSelector(pedagoAPI, (state) => state.session);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const multiMeetingAPI = useMultiMeetingAPI();
  const multiMeetingAPIRef = useRefAutoUpdate(multiMeetingAPI);

  const pedagogyUsers = usePedagogyUsers();
  const comingFromWaitingRoom = useMemo(() => pedagogyUsers.some((u) => u.inWaitingRoom), [pedagogyUsers]);

  useEffect(() => {
    const audioValidator = getPedagogyAudioValidator(currentView);
    const validator = pedagogyCtxRef.current?.addAudioValidator(audioValidator);
    return () => {
      validator?.flush();
    };
  }, [currentView]);

  const onEndCommunication = async () => {
    const meetingId = pedagoAPI?.store.getState().meeting?.id;
    if (!meetingId) return;
    const client = sdkClient.getWebsocketClient();
    const group = currentView.group;
    if (!group) return;
    const studentIds = new Set(group.users.filter((u) => u.id !== selfUser?.id).map((u) => u.id));
    if (studentIds.size) {
      await client.emitter.meeting.deleteIncomingPairings({
        id: meetingId,
        userIds: Array.from(studentIds),
      });
    }
    await client.emitter.meeting.updateRoom({
      roomId: group.id,
      status: MeetingRoomStatus.Closed,
    });
  };

  const updateMedias = useCallback(async () => {
    if (!pedagoAPI) return;
    const manager = pedagoAPI.emissionManager;
    if (session) {
      let camerasVideoIsEnabled = true;
      let screensVideoIsEnabled = true;
      let audioIsEnabled = true;
      session.medias?.forEach((m) => {
        if (m.extra?.type === "camera" || m.extra?.type === undefined) camerasVideoIsEnabled = camerasVideoIsEnabled && m.video.enabled;
        if (m.extra?.type === "screen" || m.extra?.type === undefined) screensVideoIsEnabled = screensVideoIsEnabled && m.video.enabled;
        audioIsEnabled = audioIsEnabled && m.audio.enabled;
      });
      if (!camerasVideoIsEnabled && manager.videoIsEnabled("cameras")) {
        await pedagoAPI.disableVideo({ direction: "outgoing", group: "cameras" });
      } else if (camerasVideoIsEnabled && !manager.videoIsEnabled("cameras")) {
        await pedagoAPI.enableVideo({ direction: "outgoing", group: "cameras" });
      }
      if (!screensVideoIsEnabled && manager.videoIsEnabled("screens")) {
        await pedagoAPI.disableVideo({ direction: "outgoing", group: "screens" });
      } else if (screensVideoIsEnabled && !manager.videoIsEnabled("screens")) {
        await pedagoAPI.enableVideo({ direction: "outgoing", group: "screens" });
      }
      if (!audioIsEnabled && manager.audioIsEnabled()) {
        await pedagoAPI.mute();
      } else if (audioIsEnabled && !manager.audioIsEnabled()) {
        await pedagoAPI.unmute();
      }
    }
  }, [pedagogyCtxRef, pedagoAPI, session]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    updateMedias();
  }, [updateMedias]);

  useEffect(() => {
    pedagogyCtxRef.current?.setDisplayRemoteController(true); // display by default
  }, []);

  /**
   * Some users are present with us in the pedagogy and are coming
   * from other meetings, because they were paired.
   * We need to connect to their origi meetings so we know their user data
   */
  useEffect(() => {
    const originMeetingIds = new Set<string>();
    currentView.students.forEach((student) => {
      const pairedFrom = student.session.session.pairedFromMeetingId;
      if (pairedFrom) originMeetingIds.add(pairedFrom);
    });

    const containers: MeetingContainer[] = [];
    originMeetingIds.forEach((meetingId) => {
      const container = multiMeetingAPIRef.current.all?.getOrCreateContainer(meetingId);
      if (!container) return; // can't join
      containers.push(container);
    });

    // In case they are joined and marked for destruction
    pedagogyCtxRef.current?.unscheduleDestroyContainer(containers);

    containers.forEach((container) => {
      if (container.getStatus() !== "disconnected") return; // already joined
      container.getAPI().join();
    });
  }, [currentView.students]);

  /**
   * Schedule containers for destrution before navigating away
   */
  useEffect(() => {
    return () => {
      const ctx = pedagogyCtxRef.current;
      if (!ctx) return;
      const containers = ctx.containersExceptPedagogy;
      ctx.scheduleDestroyContainer(containers);
    };
  }, []);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  const renderBottomBar = () => {
    return (
      <PedagogyBottomBar
        meetingContainerAPI={pedagoAPI}
        context={pedagogyCtx}
        backButtonText={comingFromWaitingRoom ? "Retour à la salle d'attente" : "Retour au dashboard"}
        onBackButton={onEndCommunication}
        onRemoteController={() => {
          if (!pedagogyCtx) return;
          pedagogyCtx.setDisplayRemoteController(!pedagogyCtx.displayRemoteController);
        }}
        remoteBadgeValue={unreadMessages}
      />
    );
  };

  return (
    <div className={styles.content}>
      <div
        style={{
          position: "relative",
          display: "flex",
          flex: 1,
          minHeight: 0,
          width: "100%",
        }}
      >
        <WaitingRoomContainer onMessagesUnread={(count) => setUnreadMessages(count)} />
        <PedagogyMessageForm 
          actions={pedagogyCtx?.actions} 
          confirmableAction={pedagogyCtx?.confirmableAction} 
          onClose={useCallback(() => {
            pedagogyCtxRef.current?.setConfirmableAction(undefined)
          }, [])}
        />
      </div>
      {renderBottomBar()}
    </div>
  );
};
